import React, { useState, useEffect, useContext } from "react";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import { makeStyles, Paper, Tabs, Tab, Button, TextField, Grid } from "@material-ui/core";
import SchedulesForm from "../../components/SchedulesForm";
import CompaniesManager from "../../components/CompaniesManager";
import PlansManager from "../../components/PlansManager";
import HelpAdmin from "../HelpAdmin";
import Options from "../../components/Settings/Options";
import Whitelabel from "../../components/Settings/Whitelabel";
import InterfaceSettings from "../../components/Settings/InterfaceSettings";
import FinalizacaoAtendimento from "../../components/Settings/FinalizacaoAtendimento";
import BirthdaySettings from "../../components/Settings/BirthdaySettings";
import WebChatSettings from "../../components/Settings/WebChatSettings"; // Novo import

import { i18n } from "../../translate/i18n";
import toastUtils from "../../utils/toastUtils";

import { AuthContext } from "../../context/Auth/AuthContext";
import { useCompanySettingsContext } from "../../context/CompanySettings/CompanySettingsContext";
import usePlanPermissions from "../../hooks/usePlanPermissions";
import useSettings from "../../hooks/useSettings";
import useCompanies from "../../hooks/useCompanies"; // Using useCompanies hook directly
import ForbiddenPage from "../../components/ForbiddenPage";
import IA from "../../components/Settings/IA.js";

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    backgroundColor: theme.palette.background.paper,
  },
  mainPaper: {
    ...theme.scrollbarStyles,
    overflowY: "scroll",
    flex: 1,
  },
  tab: {
    backgroundColor: theme.mode === "light" ? "#f2f2f2" : "#7f7f7f",
    borderRadius: 4,
  },
  paper: {
    ...theme.scrollbarStyles,
    overflowY: "scroll",
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    width: "100%",
  },
  container: {
    width: "100%",
    maxHeight: "100%",
  },
  control: {
    padding: theme.spacing(1),
  },
  textfield: {
    width: "100%",
  },
}));

const SettingsCustom = () => {
  const classes = useStyles();
  const [tab, setTab] = useState("options");
  const [schedules, setSchedules] = useState([]);
  const [company, setCompany] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState({});
  const [settings, setSettings] = useState({});
  const [oldSettings, setOldSettings] = useState({});
  const [schedulesEnabled, setSchedulesEnabled] = useState(false);
  const [outOfHoursMessage, setOutOfHoursMessage] = useState("");
  // Removido estado webChatModalOpen

  const { find, updateSchedules } = useCompanies();

  const { settings: companySettings, loading: settingsLoading } = useCompanySettingsContext();
  const { getAll: getAllSettingsOld, update: updateSetting } = useSettings();
  const { user, socket } = useContext(AuthContext);
  const { permissions } = usePlanPermissions();

  useEffect(() => {
    async function findData() {
      if (!user || !user.companyId) {
        return;
      }

      setLoading(true);
      try {
        const companyId = user.companyId;

        const [company, settingListOld] = await Promise.all([
          find(companyId),
          getAllSettingsOld()
        ]);

        setCompany(company);
        setSchedules(company.schedules);
        setSettings(companySettings); // Usar settings do contexto
        setOldSettings(settingListOld);
        setSchedulesEnabled(companySettings.scheduleType === "company");
        setOutOfHoursMessage(companySettings.outOfHoursMessage || "");
        setCurrentUser(user);
      } catch (e) {
        toastUtils.error(e, "Erro ao Carregar");
      } finally {
        setLoading(false);
      }
    }

    if (user?.companyId && !settingsLoading) {
      findData();
    }
  }, [user, find, getAllSettingsOld, companySettings, settingsLoading]);

  useEffect(() => {
    if (!socket || !user || !user.companyId) return;

    const onSettingsEvent = async () => {
      const data = await getAllSettingsOld();
      setOldSettings(data);
    };

    socket.on(`company-${user.companyId}-settings`, onSettingsEvent);
    return () => {
      socket.off(`company-${user.companyId}-settings`, onSettingsEvent);
    };
  }, [socket, user, getAllSettingsOld]);

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const handleSubmitSchedules = async (data) => {
    setLoading(true);
    try {
      setSchedules(data);
      await updateSchedules({ id: company.id, schedules: data });
      toastUtils.success("Horários atualizados com sucesso.", "Horários Salvos");
    } catch (e) {
      toastUtils.error(e, "Erro ao Salvar");
    }
    setLoading(false);
  };

  const handleSaveOutOfHoursMessage = async () => {
    setLoading(true);
    try {
      await updateSetting({
        key: "outOfHoursMessage",
        value: outOfHoursMessage
      });
      toastUtils.success("Mensagem atualizada com sucesso.", "Mensagem Salva");
    } catch (e) {
      toastUtils.error(e, "Erro ao Salvar");
    }
    setLoading(false);
  };

  const isSuper = () => {
    return currentUser && currentUser.super;
  };

  return (
    <MainContainer className={classes.root}>
      {user.profile === "user" ? (
        <ForbiddenPage />
      ) : (
        <>
          <MainHeader>
            <Title>{i18n.t("settings.title")}</Title>
          </MainHeader>
          <Paper className={classes.mainPaper} elevation={1}>
            <Tabs
              value={tab}
              indicatorColor="primary"
              textColor="primary"
              scrollButtons="on"
              variant="scrollable"
              onChange={handleTabChange}
              className={classes.tab}
            >
              <Tab label={i18n.t("settings.tabs.options")} value={"options"} />
              
              {user.profile === "admin" && permissions.useOpenAi && (
                <Tab label={i18n.t("settings.tabs.ia")} value={"ia"} />
              )}
              {schedulesEnabled && <Tab label="Horários" value={"schedules"} />}
              {user.profile === "admin" &&
                companySettings.informarValorVenda && (
                  <Tab
                    label="Finalização do Atendimento"
                    value={"finalizacao"}
                  />
                )}
              {user.profile === "admin" && permissions.useWebChat && (
                <Tab
                  label={i18n.t("settings.tabs.webchat")}
                  value={"webchat"}
                />
              )}
              {(!user.companyId == 1) && user.profile === "admin" && (
                <Tab
                  label={i18n.t("settings.tabs.interface")}
                  value={"interface"}
                />
              )}
              {isSuper() ? (
                <Tab
                  label={i18n.t("settings.tabs.companies")}
                  value={"companies"}
                />
              ) : null}
              {isSuper() ? (
                <Tab label={i18n.t("settings.tabs.plans")} value={"plans"} />
              ) : null}
              {isSuper() ? (
                <Tab label={i18n.t("settings.tabs.helps")} value={"helps"} />
              ) : null}
              {isSuper() ? (
                <Tab label="Whitelabel" value={"whitelabel"} />
              ) : null}

              {isSuper() ? (
                <Tab label="Configurações de Aniversários" value={"birthday-settings"} />
              ) : null}

            </Tabs>
            <Paper className={classes.paper} elevation={0}>
              {/* Renderização condicional simples - apenas a aba ativa renderiza */}
              {tab === "schedules" && (
                <div className={classes.container}>
                  <SchedulesForm
                    loading={loading}
                    onSubmit={handleSubmitSchedules}
                    initialValues={schedules}
                  />
                  <Grid container spacing={2} style={{ marginTop: 20 }}>
                    <Grid item xs={12}>
                      <TextField
                        label="Mensagem Fora de Expediente"
                        placeholder="Digite a mensagem que será enviada fora do horário de atendimento"
                        multiline
                        rows={4}
                        fullWidth
                        variant="outlined"
                        value={outOfHoursMessage}
                        onChange={(e) => setOutOfHoursMessage(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} style={{ textAlign: "right" }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveOutOfHoursMessage}
                        disabled={loading}
                      >
                        Salvar Mensagem
                      </Button>
                    </Grid>
                  </Grid>
                </div>
              )}

              {tab === "companies" && isSuper() && (
                <div className={classes.container}>
                  <CompaniesManager />
                </div>
              )}

              {tab === "plans" && isSuper() && (
                <div className={classes.container}>
                  <PlansManager />
                </div>
              )}

              {tab === "helps" && isSuper() && (
                <div className={classes.container}>
                  <HelpAdmin standalone={false} />
                </div>
              )}

              {tab === "whitelabel" && isSuper() && (
                <div className={classes.container}>
                  <Whitelabel settings={oldSettings} />
                </div>
              )}

              {tab === "birthday-settings" && isSuper() && (
                <div className={classes.container}>
                  <BirthdaySettings />
                </div>
              )}

              {tab === "finalizacao" && (
                <div className={classes.container}>
                  <FinalizacaoAtendimento />
                </div>
              )}

              {tab === "webchat" && permissions.useWebChat && (
                <div className={classes.container}>
                  {/* Substituído o botão e modal pelo componente direto */}
                  <WebChatSettings companyId={user?.companyId} />
                </div>
              )}
              {tab === "options" && (
                <div className={classes.container}>
                  <Options
                    settings={settings}
                    oldSettings={oldSettings}
                    user={currentUser}
                    scheduleTypeChanged={(value) =>
                      setSchedulesEnabled(value === "company")
                    }
                  />
                </div>
              )}
              {tab === "ia" && permissions.useOpenAi && (
                <div className={classes.container}>
                  <IA
                    settings={settings}
                    oldSettings={oldSettings}
                    user={currentUser}
                  />
                </div>
              )}

              {tab === "interface" && user.profile === "admin" && !user.companyId == 1 (
                <div className={classes.container}>
                  <InterfaceSettings settings={oldSettings} />
                </div>
              )}
            </Paper>
          </Paper>
        </>
      )}
    </MainContainer>
  );
};

export default SettingsCustom;